import type { InvestmentSummary } from "@eu.mdotm.api/sphere";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useCallback } from "react";

export type ResolvedSummary = {
	/**
	 * The summary the page renders: the proposal's while the portfolio is known to have one,
	 * the portfolio's own otherwise.
	 */
	query: UseQueryResult<InvestmentSummary>;
	/**
	 * Whether the portfolio has a proposal right now, straight from its own summary — the only
	 * source that stays readable once the proposal is gone.
	 */
	hasVisibleProposal: boolean;
	/**
	 * Refreshes the portfolio summary, and the proposal's only if the refreshed portfolio still has one.
	 */
	refetch(): Promise<void>;
};

/**
 * Reads the summary behind the `proposal=true` view without ever asking the server for a proposal
 * that no longer exists.
 *
 * `proposal=true` is a URL claim about a server-owned resource: accepting or rejecting deletes the
 * proposal, and so does a second tab, a bulk accept from the portfolio list, or simply opening an
 * old bookmark. Deriving the query key straight from that claim made the page ask
 * `GET /v3/investments/enhancement/reports/summary/{uuid}` for something already deleted, and the
 * failure landed on the summary query's error path — a "something went wrong" toast plus a redirect
 * off the page.
 *
 * So the claim is checked before it is used. The portfolio's own summary is always mounted — it is
 * valid whatever the proposal's fate — and the proposal's summary is `enabled` only while that
 * summary says `hasVisibleProposal`. Accept/reject invalidate the entity, the portfolio summary
 * refetches, the gate closes, and the proposal request is never issued: no ordering rule between the
 * action, the URL flip and the invalidation, and nothing to keep in sync with React's render timing.
 *
 * The cost is one extra request, and — only on a cold `proposal=true` deep link, where nothing is
 * cached yet — one round trip of waterfall before the proposal summary can start.
 *
 * @param onProposalUnavailable called when the portfolio turns out to have no proposal to show while
 * the view is asking for one, so the caller can drop `proposal=true` and stay on the current view.
 */
export function useResolvedSummary(
	portfolioUid: string,
	{ enhanced, onProposalUnavailable }: { enhanced: boolean; onProposalUnavailable(): void },
): ResolvedSummary {
	const currentSummary = useQuery({
		...portfolioRepository.summary.getQueryConfig(portfolioUid, { enhanced: false }),
		keepPreviousData: true,
	});

	const hasVisibleProposal = currentSummary.data?.hasVisibleProposal === true;

	const proposalSummary = useQuery({
		...portfolioRepository.summary.getQueryConfig(portfolioUid, { enhanced: true }),
		enabled: enhanced && hasVisibleProposal,
		keepPreviousData: true,
		// A proposal that vanishes between the gate and the request is worth one attempt, not four:
		// the resource is gone, there is nothing for a retry to find.
		retry: false,
	});

	useQueryReactor(currentSummary, {
		onSuccess(data) {
			if (enhanced && data.hasVisibleProposal !== true) {
				onProposalUnavailable();
			}
		},
	});

	// The gate narrows the window but cannot close it: the proposal can still be accepted elsewhere
	// between the check and the request. Degrade to the current view instead of failing the page.
	useQueryReactor(proposalSummary, { onError: onProposalUnavailable });

	return {
		// Until the portfolio summary has landed, a `proposal=true` load has no verdict yet — showing
		// the current summary then would flash the wrong view over a proposal deep link, so the
		// (still disabled, still loading) proposal query keeps the page in its loading state.
		query:
			enhanced && (hasVisibleProposal || currentSummary.data === undefined) && !proposalSummary.isError
				? proposalSummary
				: currentSummary,
		hasVisibleProposal,
		refetch: useCallback(async () => {
			const { data } = await currentSummary.refetch();
			// `refetch` ignores `enabled` in react-query v4, so the gate is re-read by hand — from the
			// answer that just landed, which is what keeps this correct even when `enhanced` is stale.
			if (enhanced && data?.hasVisibleProposal === true) {
				await proposalSummary.refetch();
			}
		}, [currentSummary, proposalSummary, enhanced]),
	};
}
